// src/pages/ClientRoleEditPage.jsx
import React, { useState, useEffect } from "react";
import { Link, useParams, useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { staffApi } from "../Apis/Api";
import Breadcrumb from "../components/Breadcrumb";
import { CancelButtonForm, UpdateButtonForm } from "../components/ui/Button";
import { handleApiResponse, handleCustomResponse } from "../utils/response";

const ClientRoleEditPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const roleDataFromState = location.state?.roleData;

  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    role_name: "",
    description: "",
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const loadRoleData = () => {
      if (roleDataFromState) {
        setFormData({
          role_name: roleDataFromState.name || "",
          description: roleDataFromState.description || "",
        });
        return;
      }

      if (id) {
        fetchRoleData(id);
      }
    };

    const fetchRoleData = async (roleId) => {
      setLoading(true);
      try {
        const response = await staffApi.getAllClientRoles({ search: roleId });
        if (
          response.data &&
          response.data.data &&
          response.data.data.length > 0
        ) {
          const role = response.data.data[0];
          setFormData({
            role_name: role.role_name || "",
            description: role.description || "",
          });
          handleApiResponse(response, "success", "GET");
        } else {
          handleCustomResponse("error", "Role not found");
        }
      } catch (error) {
        handleApiResponse(error, "Err", "GET");
      } finally {
        setLoading(false);
      }
    };

    loadRoleData();
  }, [id, roleDataFromState]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.role_name.trim())
      newErrors.role_name = "Role name is required";
    // Description is optional, so no validation required unless specified
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      // Show validation errors as toasts
      Object.values(newErrors).forEach((error) => {
        handleCustomResponse("error", error);
      });
      return;
    }

    setLoading(true);
    try {
      const roleId = roleDataFromState?.id || id;
      if (roleId === undefined || roleId === null) {
        throw new Error("Role ID is missing");
      }

      const response = await staffApi.updateClientRole(roleId, {
        role_name: formData.role_name,
        description: formData.description,
      });

      // Assuming response has a success boolean and message
      if (response.success) {
        handleApiResponse(response, "success", "PUT");
        navigate("/staff-roles");
      } else {
        handleCustomResponse("error", "Failed to update role");
      }
    } catch (error) {
      handleApiResponse(error, "Err", "PUT");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Breadcrumb title="Edit Role" />
      <div className="card h-100 p-0 radius-12">
        <div className="card-body p-24">
          <form onSubmit={handleSubmit}>
            <div className="row g-20">
              {/* {(roleDataFromState?.id || id) && (
                <div className="col-md-6 mb-20">
                  <label
                    htmlFor="id"
                    className="form-label fw-semibold text-primary-light text-sm mb-8"
                  >
                    Role ID
                  </label>
                  <input
                    type="text"
                    id="id"
                    name="id"
                    value={roleDataFromState?.id || id || ""}
                    className="form-control radius-8"
                    disabled
                  />
                </div>
              )} */}
              <div className="col-md-6 mb-20">
                <label
                  htmlFor="role_name"
                  className="form-label fw-semibold text-primary-light text-sm mb-8"
                >
                  Role Name <span className="text-danger-600">*</span>
                </label>
                <input
                  type="text"
                  id="role_name"
                  name="role_name"
                  value={formData.role_name}
                  onChange={handleChange}
                  placeholder="Enter Role Name"
                  className={`form-control radius-8 ${
                    errors.role_name ? "border-danger" : ""
                  }`}
                />
                {errors.role_name && (
                  <span className="text-sm text-danger-600 mt-2 d-block">
                    {errors.role_name}
                  </span>
                )}
              </div>
              <div className="col-md-6 mb-20">
                <label
                  htmlFor="description"
                  className="form-label fw-semibold text-primary-light text-sm mb-8"
                >
                  Description
                </label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Enter Description"
                  className="form-control radius-8"
                  rows="4"
                />
              </div>
            </div>
            <div className="d-flex align-items-center justify-content-center gap-3 mt-4">
              <CancelButtonForm />
              <UpdateButtonForm disabled={loading} />
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default ClientRoleEditPage;
